import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, Star, Circle, Filter, Eye, EyeOff, RefreshCw, AlertCircle } from 'lucide-react';
import MapaLeaflet from './MapaLeaflet';

//const API_BASE_URL = 'http://svsgestioniis:3001/api';
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

// Componente de filtro geográfico SIMPLIFICADO
const FiltroGeografico = ({ filtros, setFiltros, opcionesFiltros, proyectos, cotizaciones, promesas }) => {
  
  // Solo mostrar si hay una comuna seleccionada
  if (!filtros.comuna || filtros.comuna === '') {
    return null;
  }

  return (
    <div className="space-y-2 mb-4">
      {/* Checkbox para cotizaciones de otras comunas */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="mostrarCotizacionesOtrasComunas"
          checked={filtros.mostrarCotizacionesOtrasComunas !== false}
          onChange={(e) => setFiltros({
            ...filtros, 
            mostrarCotizacionesOtrasComunas: e.target.checked
          })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="mostrarCotizacionesOtrasComunas" className="ml-2 text-sm text-gray-700">
          • Cotizaciones otras comunas: {filtros.mostrarCotizacionesOtrasComunas ? '✅' : '❌'}
        </label>
      </div>

      {/* Checkbox para promesas de otras comunas */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="mostrarPromesasOtrasComunas"
          checked={filtros.mostrarPromesasOtrasComunas !== false}
          onChange={(e) => setFiltros({
            ...filtros, 
            mostrarPromesasOtrasComunas: e.target.checked
          })}
          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
        />
        <label htmlFor="mostrarPromesasOtrasComunas" className="ml-2 text-sm text-gray-700">
          • Promesas otras comunas: {filtros.mostrarPromesasOtrasComunas ? '✅' : '❌'}
        </label>
      </div>
    </div>
  );
};

// NUEVO: Componente de filtro de proyectos (aparece cuando hay un proyecto específico seleccionado)
const FiltroProyecto = ({ filtros, setFiltros, proyectos, cotizaciones, promesas }) => {
  
  // Solo mostrar si hay un proyecto específico seleccionado
  if (!filtros.proyecto || filtros.proyecto === '') {
    return null;
  }

  // Buscar información del proyecto seleccionado
  const proyectoSeleccionado = proyectos.find(p => p.proyecto === filtros.proyecto);
  
  if (!proyectoSeleccionado || !proyectoSeleccionado.comuna) {
    return null;
  }

  // Cotizaciones de otros proyectos en la misma comuna
  const cotizacionesOtrosProyectos = cotizaciones.filter(c => {
    const proyectoRelacionado = proyectos.find(p => p.proyecto === c.proyecto);
    return proyectoRelacionado?.comuna?.toLowerCase() === proyectoSeleccionado.comuna.toLowerCase() && 
           c.proyecto !== filtros.proyecto;
  }).length;

  // Promesas de otros proyectos en la misma comuna
  const promesasOtrosProyectos = promesas.filter(p => {
    const proyectoRelacionado = proyectos.find(pr => pr.proyecto === p.proyecto);
    return proyectoRelacionado?.comuna?.toLowerCase() === proyectoSeleccionado.comuna.toLowerCase() && 
           p.proyecto !== filtros.proyecto;
  }).length;

  return (
    <div className="space-y-2 mb-4">
      {/* Información contextual */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-xs">
        <div className="text-purple-700">
          <div className="font-medium mb-1">Proyecto: {filtros.proyecto}</div>
          <div className="space-y-1">
            <div>• Cotizaciones otros proyectos en {proyectoSeleccionado.comuna}: {cotizacionesOtrosProyectos}</div>
            <div>• Promesas otros proyectos en {proyectoSeleccionado.comuna}: {promesasOtrosProyectos}</div>
          </div>
        </div>
      </div>

      {/* Checkbox para cotizaciones de otros proyectos */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="mostrarCotizacionesOtrosProyectos"
          checked={filtros.mostrarCotizacionesOtrosProyectos !== false}
          onChange={(e) => setFiltros({
            ...filtros, 
            mostrarCotizacionesOtrosProyectos: e.target.checked
          })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="mostrarCotizacionesOtrosProyectos" className="ml-2 text-sm text-gray-700">
          • Cotizaciones otros proyectos de la comuna: {filtros.mostrarCotizacionesOtrosProyectos ? '✅' : '❌'}
        </label>
      </div>

      {/* Checkbox para promesas de otros proyectos */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="mostrarPromesasOtrosProyectos"
          checked={filtros.mostrarPromesasOtrosProyectos !== false}
          onChange={(e) => setFiltros({
            ...filtros, 
            mostrarPromesasOtrosProyectos: e.target.checked
          })}
          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
        />
        <label htmlFor="mostrarPromesasOtrosProyectos" className="ml-2 text-sm text-gray-700">
          • Promesas otros proyectos en la comuna: {filtros.mostrarPromesasOtrosProyectos ? '✅' : '❌'}
        </label>
      </div>
    </div>
  );
};

const MapaProyectos = () => {
  // Estados para los datos
  const [proyectos, setProyectos] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [promesas, setPromesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para filtros - EXTENDIDOS con filtros geográficos y de proyecto
  const [filtros, setFiltros] = useState({
    region: '',
    comuna: '',
    proyecto: '',
    mostrarProyectos: true,
    mostrarCotizaciones: 'filtradas',
    mostrarPromesas: 'filtradas',
    // FILTROS GEOGRÁFICOS - Inicializados en OFF
    mostrarCotizacionesOtrasComunas: false, // Por defecto ocultar
    mostrarPromesasOtrasComunas: false,     // Por defecto ocultar
    // NUEVOS FILTROS DE PROYECTO - Inicializados en OFF
    mostrarCotizacionesOtrosProyectos: false, // Por defecto ocultar cotizaciones de otros proyectos en la comuna
    mostrarPromesasOtrosProyectos: false      // Por defecto ocultar promesas de otros proyectos en la comuna
  });

  // Estados para la vista
  const [panelVisible, setPanelVisible] = useState(true);
  const [estadisticasExpandidas, setEstadisticasExpandidas] = useState(false);

  // Estados para opciones de filtros
  const [opcionesFiltros, setOpcionesFiltros] = useState({
    regiones: [],
    comunas: [],
    proyectos: [],
    regionesComunas: {},
    regionesProyectos: {},
    comunasProyectos: {}
  });

  // Función para cargar proyectos desde el backend
  const cargarProyectos = async (filtrosAplicar = {}) => {
    try {
      const params = new URLSearchParams();
      if (filtrosAplicar.region) params.append('region', filtrosAplicar.region);
      if (filtrosAplicar.comuna) params.append('comuna', filtrosAplicar.comuna);
      if (filtrosAplicar.proyecto) params.append('proyecto', filtrosAplicar.proyecto);
      
      const url = `${API_BASE_URL}/proyectos?${params.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error cargando proyectos:', err);
      throw err;
    }
  };

  // Función para cargar cotizaciones desde el backend
  const cargarCotizaciones = async (filtrosAplicar = {}) => {
    try {
      const params = new URLSearchParams();
      if (filtrosAplicar.region) params.append('region', filtrosAplicar.region);
      if (filtrosAplicar.comuna) params.append('comuna', filtrosAplicar.comuna);
      if (filtrosAplicar.proyecto) params.append('proyecto', filtrosAplicar.proyecto);
      
      const url = `${API_BASE_URL}/cotizaciones?${params.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error cargando cotizaciones:', err);
      throw err;
    }
  };

  // Función para cargar promesas desde el backend
  const cargarPromesas = async (filtrosAplicar = {}) => {
    try {
      const params = new URLSearchParams();
      if (filtrosAplicar.region) params.append('region', filtrosAplicar.region);
      if (filtrosAplicar.comuna) params.append('comuna', filtrosAplicar.comuna);
      if (filtrosAplicar.proyecto) params.append('proyecto', filtrosAplicar.proyecto);
      
      const url = `${API_BASE_URL}/promesas?${params.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error cargando promesas:', err);
      throw err;
    }
  };

  // Función para cargar opciones de filtros desde el backend
  const cargarOpcionesFiltros = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/filtros`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (err) {
      console.error('Error cargando opciones de filtros:', err);
      throw err;
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Cargar opciones de filtros
        const opciones = await cargarOpcionesFiltros();
        setOpcionesFiltros(opciones);
        
        // Cargar todos los proyectos, cotizaciones y promesas inicialmente
        const [proyectosData, cotizacionesData, promesasData] = await Promise.all([
          cargarProyectos(),
          cargarCotizaciones(),
          cargarPromesas()
        ]);
        
        setProyectos(proyectosData);
        setCotizaciones(cotizacionesData);
        setPromesas(promesasData);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    cargarDatosIniciales();
  }, []);

  // Aplicar filtros
  const aplicarFiltros = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filtrosParaAPI = {
        region: filtros.region,
        comuna: filtros.comuna,
        proyecto: filtros.proyecto
      };

      let proyectosFiltrados = [];
      let cotizacionesFiltradas = [];
      let promesasFiltradas = [];

      // Cargar proyectos filtrados si están habilitados
      if (filtros.mostrarProyectos) {
        proyectosFiltrados = await cargarProyectos(filtrosParaAPI);
      }

      // Cargar cotizaciones según la configuración
      if (filtros.mostrarCotizaciones === 'todas') {
        cotizacionesFiltradas = await cargarCotizaciones({});
      } else if (filtros.mostrarCotizaciones === 'filtradas') {
        cotizacionesFiltradas = await cargarCotizaciones(filtrosParaAPI);
      }

      // Cargar promesas según la configuración
      if (filtros.mostrarPromesas === 'todas') {
        promesasFiltradas = await cargarPromesas({});
      } else if (filtros.mostrarPromesas === 'filtradas') {
        promesasFiltradas = await cargarPromesas(filtrosParaAPI);
      }

      // Cargar proyectos adicionales para cotizaciones y promesas
      const proyectosEnCotizaciones = [...new Set(cotizacionesFiltradas
        .map(c => c.proyecto)
        .filter(p => p && p.trim() !== '')
      )];
      
      const proyectosEnPromesas = [...new Set(promesasFiltradas
        .map(p => p.proyecto)
        .filter(p => p && p.trim() !== '')
      )];

      // Combinar todos los nombres de proyectos necesarios
      const todosLosProyectosNecesarios = [...new Set([
        ...proyectosFiltrados.map(p => p.proyecto),
        ...proyectosEnCotizaciones,
        ...proyectosEnPromesas
      ])];

      // Cargar información completa de todos los proyectos necesarios
      let proyectosCompletos = proyectosFiltrados;
      
      if (todosLosProyectosNecesarios.length > proyectosFiltrados.length) {
        // Cargar todos los proyectos sin filtros para obtener la información completa
        const todosLosProyectos = await cargarProyectos({});
        
        // Filtrar solo los proyectos que necesitamos
        const proyectosAdicionales = todosLosProyectos.filter(p => 
          todosLosProyectosNecesarios.includes(p.proyecto) &&
          !proyectosFiltrados.some(pf => pf.proyecto === p.proyecto)
        );
        
        // Combinar proyectos filtrados con proyectos adicionales necesarios
        proyectosCompletos = [...proyectosFiltrados, ...proyectosAdicionales];
      }

      setProyectos(proyectosCompletos);
      setCotizaciones(cotizacionesFiltradas);
      setPromesas(promesasFiltradas);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar filtros (actualizada)
  const limpiarFiltros = () => {
    setFiltros({
      region: '',
      comuna: '',
      proyecto: '',
      mostrarProyectos: true,
      mostrarCotizaciones: 'filtradas',
      mostrarPromesas: 'filtradas',
      // Inicializar filtros geográficos en OFF
      mostrarCotizacionesOtrasComunas: false,
      mostrarPromesasOtrasComunas: false,
      // Inicializar filtros de proyecto en OFF
      mostrarCotizacionesOtrosProyectos: false,
      mostrarPromesasOtrosProyectos: false
    });
  };

  // Comunas filtradas por región seleccionada
  const comunasFiltradas = useMemo(() => {
    if (!filtros.region || !opcionesFiltros.regionesComunas) {
      return opcionesFiltros.comunas || [];
    }
    
    // Buscar la región seleccionada y devolver sus comunas
    const regionSeleccionada = opcionesFiltros.regiones.find(r => r.nombre === filtros.region);
    if (regionSeleccionada && regionSeleccionada.comunas) {
      return regionSeleccionada.comunas.map(comuna => ({ nombre: comuna }));
    }
    
    return [];
  }, [filtros.region, opcionesFiltros]);

  // Proyectos filtrados por región y comuna seleccionada
  const proyectosFiltrados = useMemo(() => {
    // Si hay comuna seleccionada, filtrar por esa comuna
    if (filtros.comuna && opcionesFiltros.comunasProyectos) {
      const proyectosDeLaComuna = opcionesFiltros.comunasProyectos[filtros.comuna];
      if (proyectosDeLaComuna && Array.isArray(proyectosDeLaComuna)) {
        return proyectosDeLaComuna.sort().map(proyecto => ({ nombre: proyecto }));
      }
      return [];
    }
    
    // Si hay región seleccionada pero no comuna, filtrar por región
    if (filtros.region && opcionesFiltros.regionesProyectos) {
      const proyectosDeLaRegion = opcionesFiltros.regionesProyectos[filtros.region];
      if (proyectosDeLaRegion && Array.isArray(proyectosDeLaRegion)) {
        return proyectosDeLaRegion.sort().map(proyecto => ({ nombre: proyecto }));
      }
      return [];
    }
    
    // Si no hay filtros de ubicación, mostrar todos los proyectos
    return opcionesFiltros.proyectos || [];
  }, [filtros.region, filtros.comuna, opcionesFiltros]);

  // Función para recargar datos
  const recargarDatos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await fetch(`${API_BASE_URL}/reload`, { method: 'POST' });
      const opciones = await cargarOpcionesFiltros();
      setOpcionesFiltros(opciones);
      await aplicarFiltros();
    } catch (err) {
      setError('Error recargando datos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Datos filtrados considerando los filtros geográficos y de proyecto
  const datosMapaFiltrados = useMemo(() => {
    let cotizacionesFiltradas = [...cotizaciones];
    let promesasFiltradas = [...promesas];

    // FILTROS GEOGRÁFICOS: Aplicar solo si hay una comuna seleccionada
    if (filtros.comuna && filtros.comuna !== '') {
      
      // Filtrar cotizaciones de otras comunas si está desactivado
      if (filtros.mostrarCotizacionesOtrasComunas === false) {
        cotizacionesFiltradas = cotizacionesFiltradas.filter(c => {
          const proyectoRelacionado = proyectos.find(p => p.proyecto === c.proyecto);
          // Solo mostrar cotizaciones donde el proyecto esté en la comuna seleccionada
          return proyectoRelacionado?.comuna?.toLowerCase().includes(filtros.comuna.toLowerCase());
        });
      }

      // Filtrar promesas de otras comunas si está desactivado
      if (filtros.mostrarPromesasOtrasComunas === false) {
        promesasFiltradas = promesasFiltradas.filter(p => {
          const proyectoRelacionado = proyectos.find(pr => pr.proyecto === p.proyecto);
          // Solo mostrar promesas donde el proyecto esté en la comuna seleccionada
          return proyectoRelacionado?.comuna?.toLowerCase().includes(filtros.comuna.toLowerCase());
        });
      }
    }

    // FILTROS DE PROYECTO: Aplicar solo si hay un proyecto específico seleccionado
    if (filtros.proyecto && filtros.proyecto !== '') {
      const proyectoSeleccionado = proyectos.find(p => p.proyecto === filtros.proyecto);
      
      if (proyectoSeleccionado && proyectoSeleccionado.comuna) {
        // Filtrar cotizaciones de otros proyectos en la comuna si está desactivado
        if (filtros.mostrarCotizacionesOtrosProyectos === false) {
          cotizacionesFiltradas = cotizacionesFiltradas.filter(c => {
            // Solo mostrar cotizaciones del proyecto seleccionado
            return c.proyecto === filtros.proyecto;
          });
        }

        // Filtrar promesas de otros proyectos en la comuna si está desactivado
        if (filtros.mostrarPromesasOtrosProyectos === false) {
          promesasFiltradas = promesasFiltradas.filter(p => {
            // Solo mostrar promesas del proyecto seleccionado
            return p.proyecto === filtros.proyecto;
          });
        }
      }
    }

    return {
      proyectos,
      cotizaciones: cotizacionesFiltradas,
      promesas: promesasFiltradas
    };
  }, [proyectos, cotizaciones, promesas, filtros]);

  // Pasar filtros simples directamente a MapaLeaflet incluyendo filtros geográficos y de proyecto
  const filtrosParaMapa = useMemo(() => {
    return {
      region: filtros.region,
      comuna: filtros.comuna,
      proyecto: filtros.proyecto,
      // Incluir los filtros geográficos
      mostrarCotizacionesOtrasComunas: filtros.mostrarCotizacionesOtrasComunas,
      mostrarPromesasOtrasComunas: filtros.mostrarPromesasOtrasComunas,
      // Incluir los filtros de proyecto
      mostrarCotizacionesOtrosProyectos: filtros.mostrarCotizacionesOtrosProyectos,
      mostrarPromesasOtrosProyectos: filtros.mostrarPromesasOtrosProyectos
    };
  }, [filtros]);

  return (
    // CRÍTICO: Usar h-full y w-full para ocupar todo el espacio disponible del contenedor padre
    <div className="h-full w-full bg-gray-100 flex overflow-hidden">
      {/* Panel de Filtros */}
      <div className={`bg-white shadow-lg transition-all duration-300 ${panelVisible ? 'w-80' : 'w-0'} overflow-hidden flex-shrink-0`}>
        <div className="p-4 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <Filter className="mr-2" size={24} />
              Filtros
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={recargarDatos}
                disabled={loading}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                title="Recargar datos"
              >
                <RefreshCw className={loading ? 'animate-spin' : ''} size={18} />
              </button>
              <button
                onClick={() => setPanelVisible(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Ocultar panel"
              >
                <EyeOff size={18} />
              </button>
            </div>
          </div>

          {/* Estado de Carga */}
          {loading && (
            <div className="flex items-center justify-center py-3 bg-blue-50 rounded-lg mb-3">
              <RefreshCw className="animate-spin mr-2" size={20} />
              <span className="text-blue-700">Cargando datos...</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3">
              <div className="flex items-start">
                <AlertCircle className="mr-2 mt-0.5" size={18} />
                <div>
                  <strong>Error:</strong>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Filtros de Ubicación */}
          <div className="space-y-3 mb-4">
            <h3 className="font-semibold text-gray-700 border-b pb-1">📍 Ubicación</h3>

            {/* Región - ocultar visualmente si hay proyecto, pero mantener el valor */}
            <div style={{ display: filtros.proyecto ? 'none' : 'block' }}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Región
              </label>
              <select
                value={filtros.region}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    region: e.target.value,
                    comuna: '',
                    proyecto: '',
                    mostrarCotizacionesOtrasComunas: false,
                    mostrarPromesasOtrasComunas: false,
                    mostrarCotizacionesOtrosProyectos: false,
                    mostrarPromesasOtrosProyectos: false,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">Todas las regiones</option>
                {opcionesFiltros.regiones?.map((region, index) => (
                  <option key={index} value={region.nombre}>
                    {region.nombre} ({region.comunas?.length || 0} comunas,{' '}
                    {region.proyectos?.length || 0} proyectos)
                  </option>
                ))}
              </select>
            </div>

            {/* Comuna - ocultar visualmente si hay proyecto, pero mantener el valor */}
            <div style={{ display: filtros.proyecto ? 'none' : 'block' }}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comuna
              </label>
              <select
                value={filtros.comuna}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    comuna: e.target.value,
                    proyecto: '',
                    mostrarCotizacionesOtrasComunas: false,
                    mostrarPromesasOtrasComunas: false,
                    mostrarCotizacionesOtrosProyectos: false,
                    mostrarPromesasOtrosProyectos: false,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading || !filtros.region}
              >
                <option value="">
                  {filtros.region
                    ? 'Todas las comunas'
                    : 'Selecciona una región primero'}
                </option>
                {comunasFiltradas?.map((comuna, index) => {
                  const proyectosComuna =
                    opcionesFiltros.comunasProyectos?.[comuna.nombre];
                  const cantidadProyectos = Array.isArray(proyectosComuna)
                    ? proyectosComuna.length
                    : 0;

                  return (
                    <option key={index} value={comuna.nombre}>
                      {comuna.nombre} ({cantidadProyectos} proyectos)
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Proyecto (siempre visible) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proyecto
              </label>
              <select
                value={filtros.proyecto}
                onChange={(e) => {
                  const nuevoProyecto = e.target.value;
                  setFiltros((prev) => ({
                    ...prev,
                    proyecto: nuevoProyecto,
                    mostrarCotizacionesOtrosProyectos: false,
                    mostrarPromesasOtrosProyectos: false,
                  }));
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">
                  {!filtros.region && !filtros.comuna
                    ? 'Selecciona región/comuna primero'
                    : 'Todos los proyectos'}
                </option>
                {proyectosFiltrados?.map((proyecto, index) => (
                  <option key={index} value={proyecto.nombre}>
                    {proyecto.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* NUEVO COMPONENTE DE FILTRO GEOGRÁFICO */}
          <FiltroGeografico 
            filtros={filtros}
            setFiltros={setFiltros}
            opcionesFiltros={opcionesFiltros}
            proyectos={proyectos}
            cotizaciones={cotizaciones}
            promesas={promesas}
          />

          {/* NUEVO COMPONENTE DE FILTRO DE PROYECTO */}
          <FiltroProyecto 
            filtros={filtros}
            setFiltros={setFiltros}
            proyectos={proyectos}
            cotizaciones={cotizaciones}
            promesas={promesas}
          />

          {/* Filtros de Visualización */}
          <div className="space-y-3 mb-4">
            <h3 className="font-semibold text-gray-700 border-b pb-1">👁️ Visualización</h3>
            
            {/* Mostrar Proyectos */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mostrarProyectos"
                checked={filtros.mostrarProyectos}
                onChange={(e) => setFiltros({...filtros, mostrarProyectos: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="mostrarProyectos" className="ml-2 text-sm text-gray-700 flex items-center">
                <Star size={16} className="mr-1 text-yellow-500" />
                Mostrar ubicaciones de proyectos
              </label>
            </div>

            {/* Mostrar Cotizaciones */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Circle size={16} className="inline mr-1 text-blue-500" />
                Mostrar cotizaciones
              </label>
              <div className="space-y-1">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="cotizaciones_filtradas"
                    name="mostrarCotizaciones"
                    value="filtradas"
                    checked={filtros.mostrarCotizaciones === 'filtradas'}
                    onChange={(e) => setFiltros({...filtros, mostrarCotizaciones: e.target.value})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label htmlFor="cotizaciones_filtradas" className="ml-2 text-sm text-gray-700">
                    Solo de proyectos filtrados
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="cotizaciones_todas"
                    name="mostrarCotizaciones"
                    value="todas"
                    checked={filtros.mostrarCotizaciones === 'todas'}
                    onChange={(e) => setFiltros({...filtros, mostrarCotizaciones: e.target.value})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label htmlFor="cotizaciones_todas" className="ml-2 text-sm text-gray-700">
                    Todas las cotizaciones
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="cotizaciones_ninguna"
                    name="mostrarCotizaciones"
                    value="ninguna"
                    checked={filtros.mostrarCotizaciones === 'ninguna'}
                    onChange={(e) => setFiltros({...filtros, mostrarCotizaciones: e.target.value})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label htmlFor="cotizaciones_ninguna" className="ml-2 text-sm text-gray-700">
                    No mostrar cotizaciones
                  </label>
                </div>
              </div>
            </div>

            {/* Mostrar Promesas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="inline-block w-4 h-4 bg-yellow-500 rounded-full mr-1"></div>
                Mostrar promesas
              </label>
              <div className="space-y-1">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="promesas_filtradas"
                    name="mostrarPromesas"
                    value="filtradas"
                    checked={filtros.mostrarPromesas === 'filtradas'}
                    onChange={(e) => setFiltros({...filtros, mostrarPromesas: e.target.value})}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500"
                    disabled={loading}
                  />
                  <label htmlFor="promesas_filtradas" className="ml-2 text-sm text-gray-700">
                    Solo de proyectos filtrados
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="promesas_todas"
                    name="mostrarPromesas"
                    value="todas"
                    checked={filtros.mostrarPromesas === 'todas'}
                    onChange={(e) => setFiltros({...filtros, mostrarPromesas: e.target.value})}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500"
                    disabled={loading}
                  />
                  <label htmlFor="promesas_todas" className="ml-2 text-sm text-gray-700">
                    Todas las promesas
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="promesas_ninguna"
                    name="mostrarPromesas"
                    value="ninguna"
                    checked={filtros.mostrarPromesas === 'ninguna'}
                    onChange={(e) => setFiltros({...filtros, mostrarPromesas: e.target.value})}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500"
                    disabled={loading}
                  />
                  <label htmlFor="promesas_ninguna" className="ml-2 text-sm text-gray-700">
                    No mostrar promesas
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="space-y-2">
            <button
              onClick={aplicarFiltros}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              {loading ? (
                <RefreshCw className="animate-spin mr-2" size={18} />
              ) : (
                <Search className="mr-2" size={18} />
              )}
              {loading ? 'Aplicando...' : 'Aplicar Filtros'}
            </button>
            
            <button
              onClick={limpiarFiltros}
              disabled={loading}
              className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              <Filter className="mr-2" size={16} />
              Limpiar Filtros
            </button>
          </div>

          {/* Estadísticas */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              Resultados
            </h4>
            
            {/* Totales generales */}
            <div className="text-sm text-gray-600 space-y-1 mb-3">
              <div className="flex justify-between items-center">
                <span className="flex items-center">
                  <Star size={14} className="mr-1 text-yellow-500" />
                  Total Proyectos:
                </span>
                <span className="font-medium bg-yellow-100 px-2 py-1 rounded text-yellow-800">
                  {datosMapaFiltrados.proyectos.length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center">
                  <Circle size={14} className="mr-1 text-blue-500" />
                  Total Cotizaciones:
                </span>
                <span className="font-medium bg-blue-100 px-2 py-1 rounded text-blue-800">
                  {datosMapaFiltrados.cotizaciones.length}
                  {cotizaciones.length !== datosMapaFiltrados.cotizaciones.length && (
                    <span className="text-xs ml-1">de {cotizaciones.length}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center">
                  <div className="w-3.5 h-3.5 bg-yellow-500 rounded-full mr-1"></div>
                  Total Promesas:
                </span>
                <span className="font-medium bg-yellow-100 px-2 py-1 rounded text-yellow-800">
                  {datosMapaFiltrados.promesas.length}
                  {promesas.length !== datosMapaFiltrados.promesas.length && (
                    <span className="text-xs ml-1">de {promesas.length}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="flex items-center font-medium">
                  <MapPin size={14} className="mr-1 text-green-500" />
                  Total puntos:
                </span>
                <span className="font-bold bg-green-100 px-2 py-1 rounded text-green-800">
                  {datosMapaFiltrados.proyectos.length + datosMapaFiltrados.cotizaciones.length + datosMapaFiltrados.promesas.length}
                </span>
              </div>
            </div>

            {/* Proyectos por ubicación */}
            {datosMapaFiltrados.proyectos.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h5 className="font-medium text-gray-700 text-sm border-b pb-1 flex-1">
                    📊 Proyectos por Ubicación
                  </h5>
                  <button
                    onClick={() => setEstadisticasExpandidas(!estadisticasExpandidas)}
                    className="ml-2 p-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                    title={estadisticasExpandidas ? "Mostrar solo principales" : "Mostrar todos los proyectos"}
                  >
                    {estadisticasExpandidas ? "📋→📊" : "📊→📋"}
                  </button>
                </div>
                
                {(() => {
                  // Obtener proyectos únicos
                  const proyectosUnicos = [...new Set(datosMapaFiltrados.proyectos.map(p => p.proyecto))];
                  
                  // Separar proyectos según si están en las comunas filtradas
                  const proyectosEnFiltros = [];
                  const proyectosFueraFiltros = [];
                  
                  proyectosUnicos.forEach(nombreProyecto => {
                    const proyecto = datosMapaFiltrados.proyectos.find(p => p.proyecto === nombreProyecto);
                    
                    // Verificar si el proyecto está en la comuna filtrada
                    const estaEnComuna = filtros.comuna ? 
                      proyecto?.comuna?.toLowerCase().includes(filtros.comuna.toLowerCase()) : 
                      true; // Si no hay filtro de comuna, todos están "en filtros"
                    
                    if (estaEnComuna) {
                      proyectosEnFiltros.push(nombreProyecto);
                    } else {
                      proyectosFueraFiltros.push(nombreProyecto);
                    }
                  });

                  return (
                    <div className="space-y-2">
                      {/* PRIMERO: Proyectos de las comunas filtradas */}
                      {proyectosEnFiltros.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-green-700 mb-1 flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                            {filtros.comuna ? `En ${filtros.comuna}` : 'Proyectos principales'}
                            <span className="ml-1 bg-green-100 text-green-800 px-1 rounded text-xs">
                              {proyectosEnFiltros.length}
                            </span>
                          </h6>
                          
                          {proyectosEnFiltros
                            .slice(0, estadisticasExpandidas ? proyectosEnFiltros.length : 6)
                            .map((nombreProyecto, index) => {
                            const proyecto = datosMapaFiltrados.proyectos.find(p => p.proyecto === nombreProyecto);
                            const cantidadCotizaciones = cotizaciones.filter(c => c.proyecto === nombreProyecto).length;
                            const cantidadPromesas = promesas.filter(p => p.proyecto === nombreProyecto).length;
                            
                            return (
                              <div key={`en-filtros-${index}`} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '2px',
                                fontSize: '10px',
                                padding: '2px 4px',
                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                borderRadius: '3px'
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: proyecto?.color || '#FFD700',
                                  marginRight: '4px',
                                  borderRadius: '1px',
                                  border: '1px solid #ccc'
                                }}></div>
                                <span style={{ 
                                  flex: 1, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  fontSize: '10px'
                                }}>
                                  {estadisticasExpandidas ? nombreProyecto : 
                                   (nombreProyecto.length > 15 ? `${nombreProyecto.substring(0, 15)}...` : nombreProyecto)}
                                </span>
                                <div style={{ display: 'flex', gap: '1px', marginLeft: '2px' }}>
                                  {cantidadCotizaciones > 0 && (
                                    <span style={{ 
                                      backgroundColor: '#dbeafe', 
                                      color: '#1e40af',
                                      padding: '1px 2px', 
                                      borderRadius: '2px',
                                      fontSize: '8px'
                                    }}>
                                      C:{cantidadCotizaciones}
                                    </span>
                                  )}
                                  {cantidadPromesas > 0 && (
                                    <span style={{ 
                                      backgroundColor: '#fef3c7', 
                                      color: '#92400e',
                                      padding: '1px 2px', 
                                      borderRadius: '2px',
                                      fontSize: '8px'
                                    }}>
                                      P:{cantidadPromesas}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          
                          {!estadisticasExpandidas && proyectosEnFiltros.length > 6 && (
                            <div style={{ fontSize: '9px', color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                              ... y {proyectosEnFiltros.length - 6} más
                            </div>
                          )}
                        </div>
                      )}

                      {/* DESPUÉS: Proyectos de otras comunas */}
                      {proyectosFueraFiltros.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-orange-700 mb-1 flex items-center">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mr-1"></div>
                            Otras comunas
                            <span className="ml-1 bg-orange-100 text-orange-800 px-1 rounded text-xs">
                              {proyectosFueraFiltros.length}
                            </span>
                          </h6>
                          
                          {proyectosFueraFiltros
                            .slice(0, estadisticasExpandidas ? proyectosFueraFiltros.length : 4)
                            .map((nombreProyecto, index) => {
                            const proyecto = datosMapaFiltrados.proyectos.find(p => p.proyecto === nombreProyecto);
                            const cantidadCotizaciones = cotizaciones.filter(c => c.proyecto === nombreProyecto).length;
                            const cantidadPromesas = promesas.filter(p => p.proyecto === nombreProyecto).length;
                            
                            return (
                              <div key={`fuera-filtros-${index}`} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '2px',
                                fontSize: '10px',
                                padding: '2px 4px',
                                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                                borderRadius: '3px'
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: proyecto?.color || '#FFD700',
                                  marginRight: '4px',
                                  borderRadius: '1px',
                                  border: '1px solid #ccc'
                                }}></div>
                                <span style={{ 
                                  flex: 1, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  fontSize: '10px'
                                }}>
                                  {estadisticasExpandidas ? nombreProyecto :
                                   (nombreProyecto.length > 12 ? `${nombreProyecto.substring(0, 12)}...` : nombreProyecto)}
                                </span>
                                <span style={{ 
                                  fontSize: '8px', 
                                  color: '#666',
                                  marginRight: '2px'
                                }}>
                                  {proyecto?.comuna}
                                </span>
                                <div style={{ display: 'flex', gap: '1px' }}>
                                  {cantidadCotizaciones > 0 && (
                                    <span style={{ 
                                      backgroundColor: '#dbeafe', 
                                      color: '#1e40af',
                                      padding: '1px 2px', 
                                      borderRadius: '2px',
                                      fontSize: '8px'
                                    }}>
                                      C:{cantidadCotizaciones}
                                    </span>
                                  )}
                                  {cantidadPromesas > 0 && (
                                    <span style={{ 
                                      backgroundColor: '#fef3c7', 
                                      color: '#92400e',
                                      padding: '1px 2px', 
                                      borderRadius: '2px',
                                      fontSize: '8px'
                                    }}>
                                      P:{cantidadPromesas}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          
                          {!estadisticasExpandidas && proyectosFueraFiltros.length > 4 && (
                            <div style={{ fontSize: '9px', color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                              ... y {proyectosFueraFiltros.length - 4} más
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                <div style={{ marginTop: '6px', fontSize: '9px', color: '#666' }}>
                  <p><strong>C:</strong> Cotizaciones, <strong>P:</strong> Promesas</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Área del Mapa */}
      <div className="flex-1 relative flex flex-col">
        {/* Botón para mostrar panel */}
        {!panelVisible && (
          <button
            onClick={() => setPanelVisible(true)}
            className="absolute top-4 left-4 z-10 bg-white p-3 rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
            title="Mostrar panel de filtros"
          >
            <Eye size={20} />
          </button>
        )}

        {/* Mapa Real con Leaflet */}
        <div className="flex-1 w-full relative">
          {loading ? (
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="animate-spin mx-auto mb-4 text-blue-600" size={48} />
                <p className="text-gray-600 font-medium">Cargando mapa...</p>
                <p className="text-sm text-gray-500 mt-2">Procesando datos desde Excel</p>
              </div>
            </div>
          ) : error ? (
            <div className="w-full h-full bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center">
              <div className="text-center max-w-md">
                <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
                <p className="text-red-600 font-medium mb-2">Error cargando datos</p>
                <p className="text-sm text-gray-600 mb-4">Verifica que el servidor esté corriendo</p>
                <button
                  onClick={recargarDatos}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <div className="w-full h-full">
              <MapaLeaflet 
                proyectos={filtros.mostrarProyectos ? datosMapaFiltrados.proyectos : []} 
                cotizaciones={datosMapaFiltrados.cotizaciones}
                promesas={datosMapaFiltrados.promesas}
                filtros={filtrosParaMapa}
                // Pasar datos originales para estadísticas
                cotizacionesOriginales={cotizaciones}
                promesasOriginales={promesas}
              />
            </div>
          )}
        </div>

        {/* Panel de información flotante */}
        {(datosMapaFiltrados.proyectos.length > 0 || datosMapaFiltrados.cotizaciones.length > 0 || datosMapaFiltrados.promesas.length > 0) && !error && (
          <div className="absolute bottom-4 left-4 bg-white p-4 rounded-lg shadow-lg max-w-sm border border-gray-200 z-10">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              Filtros Activos
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              {filtros.region && (
                <div className="flex items-center">
                  <strong className="text-gray-700 mr-2">Región:</strong> 
                  <span className="bg-blue-100 px-2 py-1 rounded text-blue-800">{filtros.region}</span>
                </div>
              )}
              {filtros.comuna && (
                <div className="flex items-center">
                  <strong className="text-gray-700 mr-2">Comuna:</strong>
                  <span className="bg-green-100 px-2 py-1 rounded text-green-800">{filtros.comuna}</span>
                </div>
              )}
              {filtros.proyecto && (
                <div className="flex items-center">
                  <strong className="text-gray-700 mr-2">Proyecto:</strong>
                  <span className="bg-yellow-100 px-2 py-1 rounded text-yellow-800 text-xs">
                    {filtros.proyecto.length > 20 ? `${filtros.proyecto.substring(0, 20)}...` : filtros.proyecto}
                  </span>
                </div>
              )}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-xs">
                  <span><strong>Proyectos:</strong></span>
                  <span className={filtros.mostrarProyectos ? 'text-green-600' : 'text-red-600'}>
                    {filtros.mostrarProyectos ? 'Visible' : 'Oculto'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span><strong>Cotizaciones:</strong></span>
                  <span className="text-blue-600">
                    {filtros.mostrarCotizaciones === 'todas' ? 'Todas' :
                     filtros.mostrarCotizaciones === 'filtradas' ? 'Filtradas' : 'Ocultas'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span><strong>Promesas:</strong></span>
                  <span className="text-yellow-600">
                    {filtros.mostrarPromesas === 'todas' ? 'Todas' :
                     filtros.mostrarPromesas === 'filtradas' ? 'Filtradas' : 'Ocultas'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapaProyectos;